"use client";

import clsx from "clsx";
import { Calendar, MapPin } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";

import FormSelectField from "@/components/common/forms/FormSelectField";
import FormTextField from "@/components/common/forms/FormTextField";
import { BankValuationCompleteFormSchemaType } from "@/schemas/validation/bankValuationSchema";
import { CONTAINER_IDS, LOCAL_STORAGE_KEYS } from "@/utils/constants";
import CheckoutForm from "./CheckoutForm";

// ─── Types ────────────────────────────────────────────────────────────────────

type PlanType =
  | "fast"
  | "inspection_alba_high"
  | "inspection_alba_low"
  | "inspection_home_high"
  | "inspection_home_low";

interface PlanDetails {
  serviceType: PlanType;
  price: number;
  title: string;
  description: string;
  deliveryTime: string;
}

const isEnbdBank = (bankName: string | undefined) => !!bankName && bankName.toLowerCase().includes("nbd");

// ─── Component ────────────────────────────────────────────────────────────────

const BankValuationStripePaymentStep: React.FC = () => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isBusinessPayment, setIsBusinessPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);

  const { watch, control } = useFormContext<BankValuationCompleteFormSchemaType>();
  const t = useTranslations("BankValuation.paymentStep");
  const locale = useLocale();

  // ── Form values ────────────────────────────────────────────────────────────
  const userEmail = watch("email");
  const poBox = watch("poBox");
  const emirate = watch("emirate");
  const trn = watch("trn");
  const businessName = watch("businessName");
  const bankName = watch("bankName");
  const specs = watch("specs") as string | undefined;
  const marketPrice = watch("dealDriveEstimatedPrice" as any) as number | undefined;

  const inspectionType = watch("inspectionType" as any) as "alba" | "home" | undefined;
  const inspectionDate = watch("inspectionDate" as any) as string | undefined;
  const inspectionTime = watch("inspectionTime" as any) as string | undefined;
  const inspectionLocation = watch("inspectionLocation" as any) as string | undefined;

  const pendingValuationRequestId =
    typeof window !== "undefined" ? (localStorage.getItem(LOCAL_STORAGE_KEYS.pendingValuationRequestId) ?? "") : "";

  const showInspection = isEnbdBank(bankName);

  const isGcc = (specs ?? "").toLowerCase().trim().includes("gcc");
  const isHighTier = (marketPrice != null && marketPrice > 250_000) || !isGcc;

  // ── Determine plan ────────────────────────────────────────────────────────
  const getPlan = (): PlanDetails => {
    if (!showInspection) {
      return {
        serviceType: "fast",
        price: 299,
        title: t("plans.express.title"),
        description: t("plans.express.description"),
        deliveryTime: t("plans.express.deliveryTime"),
      };
    }

    const albaTitle = t("plans.albaInspection.title");
    const albaDesc = t("plans.albaInspection.description");
    const albaDelivery = t("plans.albaInspection.deliveryTime");
    const homeTitle = t("plans.homeInspection.title");
    const homeDesc = t("plans.homeInspection.description");
    const homeDelivery = t("plans.homeInspection.deliveryTime");

    if (inspectionType === "alba" && isHighTier) {
      return {
        serviceType: "inspection_alba_high",
        price: 399,
        title: albaTitle,
        description: albaDesc,
        deliveryTime: albaDelivery,
      };
    }
    if (inspectionType === "alba" && !isHighTier) {
      return {
        serviceType: "inspection_alba_low",
        price: 299,
        title: albaTitle,
        description: albaDesc,
        deliveryTime: albaDelivery,
      };
    }
    if (inspectionType === "home" && isHighTier) {
      return {
        serviceType: "inspection_home_high",
        price: 499,
        title: homeTitle,
        description: homeDesc,
        deliveryTime: homeDelivery,
      };
    }
    if (inspectionType === "home" && !isHighTier) {
      return {
        serviceType: "inspection_home_low",
        price: 499,
        title: homeTitle,
        description: homeDesc,
        deliveryTime: homeDelivery,
      };
    }

    return {
      serviceType: isHighTier ? "inspection_alba_high" : "inspection_alba_low",
      price: isHighTier ? 399 : 299,
      title: albaTitle,
      description: albaDesc,
      deliveryTime: albaDelivery,
    };
  };

  const plan = getPlan();

  // ── Emirates options ───────────────────────────────────────────────────────
  const emirateOptions = [
    { value: "Dubai", label: t("emirates.dubai") },
    { value: "Abu Dhabi", label: t("emirates.abuDhabi") },
    { value: "Sharjah", label: t("emirates.sharjah") },
    { value: "Ajman", label: t("emirates.ajman") },
    { value: "Ras Al Khaimah", label: t("emirates.rasAlKhaimah") },
    { value: "Umm Al Quwain", label: t("emirates.ummAlQuwain") },
    { value: "Fujairah", label: t("emirates.fujairah") },
  ];

  // ── Create checkout session ────────────────────────────────────────────────
  const createCheckoutSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!pendingValuationRequestId) {
        throw new Error("No pending valuation request ID found");
      }

      const businessMetadata = isBusinessPayment
        ? {
            poBox: poBox || "",
            emirate: emirate || "Dubai",
            trn: trn || "",
            businessName: businessName || "",
            isBusinessPayment: "true",
          }
        : { poBox: poBox || "", emirate: emirate || "Dubai", isBusinessPayment: "false" };

      const inspectionMetadata = showInspection
        ? {
            inspectionType: inspectionType || "",
            inspectionDate: inspectionDate || "",
            inspectionTime: inspectionTime || "",
            inspectionLocation: inspectionLocation || "",
            inspectionTier: isHighTier ? "high" : "low",
          }
        : {};

      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          embedded: true,
          serviceType: plan.serviceType,
          amount: plan.price,
          userEmail,
          locale,
          metadata: {
            service: "bank_valuation",
            valuationRequestId: pendingValuationRequestId,
            plan: plan.serviceType,
            ...businessMetadata,
            ...inspectionMetadata,
          },
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const { clientSecret, error: responseError } = await response.json();
      if (responseError) throw new Error(responseError);
      if (!clientSecret) throw new Error("No client secret returned from the server");

      setClientSecret(clientSecret);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
      if (retryCount < 3) setRetryCount((prev) => prev + 1);
    } finally {
      setIsLoading(false);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.pendingValuationRequestId);
    }
  }, [
    pendingValuationRequestId,
    retryCount,
    plan.serviceType,
    plan.price,
    userEmail,
    isBusinessPayment,
    poBox,
    emirate,
    trn,
    businessName,
    locale,
    showInspection,
    inspectionType,
    inspectionDate,
    inspectionTime,
    inspectionLocation,
    isHighTier,
  ]);

  const handleConfirmPlan = () => {
    setShowCheckout(true);
    createCheckoutSession();
    document.getElementById(CONTAINER_IDS.bankValuation)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-gray-700">{t("loading")}</p>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="mb-6 w-full max-w-md rounded-md border border-red-200 bg-red-50 p-4">
          <h3 className="font-medium text-red-800">{t("error.title")}</h3>
          <p className="mt-2 text-sm text-red-700">{error}</p>
        </div>
        <button
          onClick={() => {
            setRetryCount(0);
            createCheckoutSession();
          }}
          className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          {t("error.tryAgain")}
        </button>
      </div>
    );
  }

  // ── Plan confirmation view ─────────────────────────────────────────────────
  if (!showCheckout) {
    return (
      <div className="mx-auto w-full max-w-3xl p-4">
        <h2 className="inter mb-2 text-center text-2xl font-semibold">{t("title")}</h2>
        <p className="mb-6 text-center text-gray-600">{t("subtitle")}</p>

        {/* Inspection booking summary — ENBD only */}
        {showInspection && inspectionType && (
          <div className="mb-6 rounded-2xl border border-red-400 p-4">
            <p className="inter text-sm font-semibold text-black">
              {t("inspectionSummaryLabel")}:{" "}
              {inspectionType === "alba" ? t("plans.albaInspection.title") : t("plans.homeInspection.title")}
            </p>

            {inspectionDate && (
              <div className="inter mt-2 flex items-center justify-between gap-2 text-xs text-primary-foreground">
                <span className="flex shrink-0 items-center gap-1">
                  <Calendar size={15} color={"hsla(var(--primary))"} />
                  {t("inspectionDateLabel")}
                </span>
                <span className="text-end">
                  {inspectionDate}
                  {inspectionTime ? ` / ${inspectionTime}` : ""}
                </span>
              </div>
            )}

            {inspectionLocation && (
              <div className="inter mt-2 flex items-start justify-between gap-2 text-xs text-primary-foreground">
                <span className="flex shrink-0 items-center gap-1">
                  <MapPin size={15} color={"hsla(var(--primary))"} />
                  {t("inspectionLocationLabel")}
                </span>
                <span className="text-end">{inspectionLocation}</span>
              </div>
            )}
          </div>
        )}

        {/* Plan card */}
        <div className="mb-8">
          <div className="rounded-lg border border-success bg-success-light p-6 shadow-sm">
            <div className="mb-4 flex items-start justify-between">
              <h3 className="font-urbanist text-xl font-medium">{plan.title}</h3>
              <div className="flex h-5 w-5 items-center justify-center rounded-full border border-success bg-success">
                <div className="h-2 w-2 rounded-full bg-white" />
              </div>
            </div>
            <div className="mb-2 text-xl font-bold text-success sxs:text-2xl">AED {plan.price} + VAT</div>
            <p className="mb-4 text-gray-600">{plan.description}</p>
            <div className="flex items-start text-sm text-gray-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-1 h-4 w-4 text-success"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="-mt-0.5">{plan.deliveryTime}</span>
            </div>
          </div>
        </div>

        {/* Business payment section */}
        <div className="mb-6 rounded-lg border border-gray-100 bg-gray-50 p-6">
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <FormTextField
              name="poBox"
              control={control}
              label={t("businessPayment.poBoxLabel")}
              placeholder={t("businessPayment.poBoxPlaceholder")}
              type="text"
              className="bg-white"
            />
            <FormSelectField
              name="emirate"
              control={control}
              label={t("businessPayment.emirateLabel")}
              options={emirateOptions}
              helperText={t("businessPayment.emirateHelperText")}
              customClassNames={{
                control: (state: any) =>
                  clsx(
                    "flex items-center bg-secondary-light border border-secondary-light rounded-2xl min-h-[42px] !bg-white hover:cursor-pointer hover:border-grey-border p-3 transition-all duration-300",
                    state.isFocused ? "border-grey-border ring-1 ring-grey-border" : "",
                    state.isDisabled && "opacity-50",
                  ),
              }}
            />
          </div>

          <div className="flex items-center">
            <label className="flex cursor-pointer items-center">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isBusinessPayment}
                  onChange={() => setIsBusinessPayment((v) => !v)}
                  className="peer sr-only"
                />
                <div className="h-5 w-5 rounded border border-gray-300 bg-white transition-all peer-checked:border-success peer-checked:bg-success">
                  {isBusinessPayment && (
                    <svg
                      className="h-3 w-3 translate-x-0.5 translate-y-0.5 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <span className="ms-3 font-medium text-gray-700">{t("businessPayment.businessToggle")}</span>
            </label>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${isBusinessPayment ? "mt-4 max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
          >
            <div className="pt-2">
              <FormTextField
                name="businessName"
                control={control}
                label={t("businessPayment.businessNameLabel")}
                placeholder={t("businessPayment.businessNamePlaceholder")}
                type="text"
                className="bg-white"
              />
            </div>
            <div className="pt-4">
              <FormTextField
                name="trn"
                control={control}
                label={t("businessPayment.trnLabel")}
                placeholder={t("businessPayment.trnPlaceholder")}
                type="text"
                className="bg-white"
              />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mb-6 rounded-lg border border-gray-100 bg-gray-50 p-4">
          <h3 className="mb-4 font-medium">{t("summary.title")}</h3>
          <div className="mb-1 flex items-center justify-between">
            <span>{t("summary.selectedPlan")}</span>
            <span className="text-end font-medium">{plan.title}</span>
          </div>
          <hr className="my-2" />
          <div className="mb-1 flex items-center justify-between">
            <span>{t("summary.price")}</span>
            <span className="text-end font-medium">AED {plan.price} + VAT</span>
          </div>
          <hr className="my-2" />
          <div className="flex items-start justify-between">
            <span className="shrink-0">{t("summary.estimatedDelivery")}</span>
            <span className="text-end font-medium">{plan.deliveryTime}</span>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleConfirmPlan}
            className="inter rounded-xl bg-black px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800 sxs:text-base"
          >
            {t("proceedToPayment")}
          </button>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <p className="text-red-600">{t("error.unableToInitialize")}</p>
      </div>
    );
  }

  return <CheckoutForm clientSecret={clientSecret} />;
};

export default BankValuationStripePaymentStep;
